import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, distinctUntilChanged, firstValueFrom, map } from 'rxjs';
import { AuthService } from './auth.service';
import { SubscriptionClaims } from '../models/authuser.model';

/** Ordered tier hierarchy — higher index = higher plan */
const PLAN_TIERS: Record<string, number> = {
  starter: 0,
  pro: 1,
  'pro-plus': 2,
  enterprise: 3,
};

const DEFAULT_SUBSCRIPTION: SubscriptionClaims = {
  plan: 'starter',
  status: 'active',
  features: ['dual_editor'],
};

@Injectable({
  providedIn: 'root',
})
export class SubscriptionService {
  private readonly _authService = inject(AuthService);

  /** Internal subject kept in sync with the auth user's subscription claims */
  private readonly _subscription$ = new BehaviorSubject<SubscriptionClaims>(DEFAULT_SUBSCRIPTION);

  /**
   * Tracks the post-checkout verification lifecycle.
   * - 'idle'      → normal state
   * - 'pending'   → waiting for plan change after Stripe redirect
   * - 'confirmed' → plan changed successfully
   * - 'failed'    → retries exhausted, plan still unchanged
   */
  readonly upgradeStatus$ = new BehaviorSubject<'idle' | 'pending' | 'confirmed' | 'failed'>('idle');

  constructor() {
    // Keep _subscription$ in sync whenever the auth user changes
    this._authService.authState$.subscribe(() => {
      const sub = this._authService.currentUser()?.subscription ?? DEFAULT_SUBSCRIPTION;
      this._subscription$.next(sub);
    });
  }

  // ---------------------------------------------------------------------------
  // Public observables
  // ---------------------------------------------------------------------------

  /** Emits the current plan slug, e.g. 'starter' | 'pro' | 'enterprise' */
  readonly currentPlan$: Observable<string> = this._subscription$.pipe(
    map(s => s.plan),
    distinctUntilChanged(),
  );

  /** Emits the Stripe subscription status, e.g. 'active' | 'past_due' | 'canceled' */
  readonly status$: Observable<string> = this._subscription$.pipe(
    map(s => s.status),
    distinctUntilChanged(),
  );

  /** Emits the full list of enabled feature keys */
  readonly features$: Observable<string[]> = this._subscription$.pipe(
    map(s => s.features),
  );

  // ---------------------------------------------------------------------------
  // Synchronous snapshot helpers
  // ---------------------------------------------------------------------------

  /** Returns true when the current plan includes the given feature key */
  hasFeature(key: string): boolean {
    return this._subscription$.value.features.includes(key);
  }

  /**
   * Returns true when the user's plan tier is >= the required plan tier.
   * e.g. hasPlan('pro') is true for both 'pro' and 'enterprise' users.
   */
  hasPlan(requiredSlug: string): boolean {
    const currentTier = PLAN_TIERS[this._subscription$.value.plan] ?? 0;
    const requiredTier = PLAN_TIERS[requiredSlug] ?? 0;
    return currentTier >= requiredTier;
  }

  /** Returns true when the current user has the 'admin' role */
  isAdmin(): boolean {
    const roles = this._authService.currentUser()?.roles ?? [];
    return roles.some(r => r.toLowerCase() === 'admin');
  }

  /** Snapshot of the current plan slug */
  getCurrentPlan(): string {
    return this._subscription$.value.plan;
  }

  getOrgId(): string | undefined {
    return this._authService.currentUser()?.orgId;
  }

  /** Push externally-fetched subscription claims into the internal state. */
  updateSubscription(claims: SubscriptionClaims): void {
    this._subscription$.next(claims);
  }

  // ---------------------------------------------------------------------------
  // Post-checkout upgrade verification
  // ---------------------------------------------------------------------------

  /**
   * Force-refreshes the Firebase ID token and polls until the plan claim
   * changes from `previousPlan`, covering the Stripe webhook → backend →
   * custom claims propagation delay (~0–5 s).
   *
   * Callers should subscribe to `upgradeStatus$` to react to state changes:
   *   - 'pending'   → show "Verifying your subscription..." spinner
   *   - 'confirmed' → show success message and refresh billing display
   *   - 'failed'    → show "Please reload in a moment" warning
   *
   * @param previousPlan The plan slug the user was on before checkout
   */
  async refreshAfterUpgrade(previousPlan: string): Promise<void> {
    console.log('[Billing] refreshAfterUpgrade — previousPlan:', previousPlan);
    this.upgradeStatus$.next('pending');

    // Give the Stripe webhook + backend time to write updated custom claims
    await new Promise<void>(resolve => setTimeout(resolve, 1500));

    const check = async (attempt: number): Promise<boolean> => {
      console.log(`[Billing] check() attempt ${attempt} — calling refreshCurrentUser()`);
      await firstValueFrom(this._authService.refreshCurrentUser());
      const sub = this._authService.currentUser()?.subscription;
      console.log(`[Billing] check() attempt ${attempt} — claims after refresh:`, sub);
      const newPlan = sub?.plan ?? 'starter';
      this._subscription$.next(sub ?? DEFAULT_SUBSCRIPTION);
      console.log(`[Billing] check() attempt ${attempt} — newPlan: '${newPlan}', previousPlan: '${previousPlan}', changed: ${newPlan !== previousPlan}`);
      return newPlan !== previousPlan;
    };

    const changed = await check(1);
    if (changed) {
      console.log('[Billing] Plan changed on first attempt — confirming upgrade.');
      this.upgradeStatus$.next('confirmed');
      return;
    }

    console.log('[Billing] Plan unchanged after attempt 1 — waiting 3 s before retry.');
    await new Promise<void>(resolve => setTimeout(resolve, 3000));
    const changedRetry = await check(2);
    console.log(`[Billing] Final result — ${changedRetry ? 'confirmed' : 'failed'}`);
    this.upgradeStatus$.next(changedRetry ? 'confirmed' : 'failed');
  }
}
